import React, { useEffect, useRef } from "react";
import {
  Snackbar,
  Alert,
  IconButton,
  Box,
  Typography,
  keyframes,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import alarmAudio from "../../assets/audios/alarm-clock.mp3";

// Define keyframe animations
const pulse = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const shake = keyframes`
  0%, 100% {
    transform: translateX(0);
  }
  10%, 30%, 50%, 70%, 90% {
    transform: translateX(-2px);
  }
  20%, 40%, 60%, 80% {
    transform: translateX(2px);
  }
`;

interface AlarmSnackbarProps {
  open: boolean;
  message: string;
  content?: React.ReactNode;
  // onClose: () => void;
  onClose: (event: React.SyntheticEvent | Event, reason?: string) => void;
  autoHideDuration?: number | null;
}

export const AlarmSnackbar: React.FC<AlarmSnackbarProps> = ({
  open,
  message,
  content,
  onClose,
  autoHideDuration = 5000,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (open) {
      // Play alarm sound
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(console.error);
      }

      // if (audioRef.current) {
      //   audioRef.current.loop = true
      // }

      // Add vibration if supported
      if (navigator.vibrate) {
        navigator.vibrate([200, 100, 200]);
      }
    } else {
      // Stop alarm sound
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      }
    }
  }, [open]);

  return (
    <>
      <audio ref={audioRef} src={alarmAudio} preload="auto" />
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        open={open}
        autoHideDuration={autoHideDuration}
        onClose={onClose}
        sx={{
          "& .MuiSnackbar-root": {
            zIndex: 9999,
          },
          "& .MuiSnackbarContent-root": {
            transform: "translateY(-20px)",
            animation: "slideInDown 0.4s ease-out forwards",
          },
          "@keyframes slideInDown": {
            "0%": {
              transform: "translateY(-100px)",
              opacity: 0,
            },
            "100%": {
              transform: "translateY(0)",
              opacity: 1,
            },
          },
        }}
      >
        <Alert
          severity="success"
          icon={
            <Box
              sx={{
                animation: `${pulse} 2s ease-in-out infinite`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #4caf50, #66bb6a)",
                borderRadius: "50%",
                width: "32px",
                height: "32px",
                boxShadow: "0 2px 8px rgba(76, 175, 80, 0.3)",
              }}
            >
              <NotificationsActiveIcon
                sx={{
                  animation: `${shake} 0.8s ease-in-out infinite`,
                  color: "white",
                  fontSize: "18px",
                }}
              />
            </Box>
          }
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={onClose}
              sx={{
                color: "#2e7d32",
                "&:hover": {
                  backgroundColor: "rgba(46, 125, 50, 0.1)",
                },
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{
            background: "linear-gradient(135deg, #e8f5e8, #f1f8e9)",
            color: "#2e7d32",
            border: "2px solid #4caf50",
            borderRadius: "12px",
            boxShadow: "0 8px 24px rgba(76, 175, 80, 0.25), 0 4px 12px rgba(0, 0, 0, 0.1)",
            minWidth: "320px",
            backdropFilter: "blur(10px)",
            "& .MuiAlert-message": {
              width: "100%",
            },
            "& .MuiAlert-icon": {
              marginRight: "12px",
            },
          }}
        >
          <Box>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                mb: content ? 1.5 : 0,
                color: "#1b5e20",
                fontSize: "15px",
                textShadow: "0 1px 2px rgba(0,0,0,0.1)",
              }}
            >
              {message}
            </Typography>
            {content && (
              <Box sx={{
                mt: 1.5,
                p: 1.5,
                backgroundColor: "rgba(76, 175, 80, 0.08)",
                borderRadius: "8px",
                border: "1px solid rgba(76, 175, 80, 0.2)",
              }}>
                {content}
              </Box>
            )}
          </Box>
        </Alert>
      </Snackbar>
    </>
  );
}; 